import {AvatarWithLabel} from '@/components/ui/avatar-label';
import type {ComboboxProps} from '@/components/ui/combobox';
import {Combobox} from '@/components/ui/combobox';
import {graphqlClient} from '@/services/graphqlClient';
import type {User} from '@/services/graphqlHooks';
import {useGetAllUsersForInputQuery, UserGroup} from '@/services/graphqlHooks';

export type UserOption = User & {
  fallbackText: string;
  value: string;
  label: string;
  keywords: string[];
};

export type UserInputProps = Omit<ComboboxProps<UserOption>, 'items'> & {
  value: string;
  onChange: (value: string) => void;
  excludeOptionsIds?: string[];
  optionsIds?: string[];
};

const UserInput = ({
  value,
  onChange,
  excludeOptionsIds = [],
  optionsIds,
  ...props
}: UserInputProps) => {
  const {data} = useGetAllUsersForInputQuery(graphqlClient);

  const users = data?.getAllUsers || [];

  const userOptions = users
    .filter(
      user =>
        user.group !== UserGroup.AuthorizedRelatives &&
        !excludeOptionsIds.includes(user.id) &&
        (!optionsIds || optionsIds.includes(user.id))
    )
    .map(user => ({
      fallbackText: `${user.name.firstName[0]}${user.name.lastName[0]}`,
      keywords: [user.name.firstName, user.name.lastName],
      label: `${user.name.firstName} ${user.name.lastName}`,
      value: user.id,
      ...user,
    })) as UserOption[];

  const selectedItem =
    userOptions.find(options => options.value === value) || null;

  return (
    <Combobox
      items={userOptions}
      itemToString={option => option?.label || ''}
      selectedItem={selectedItem}
      onSelectedItemChange={({selectedItem}) => {
        onChange(selectedItem?.value || '');
      }}
      renderItem={option => (
        <AvatarWithLabel
          src={option.avatar || undefined}
          fallback={option.fallbackText}
          label={option.label}
          color="secondary"
          size="small"
          layout="inline"
        />
      )}
      {...props}
    />
  );
};

export default UserInput;
